import { useState, useMemo, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import type { GraphData, GraphNode, EntityType, Note, Task } from '../types'
import { ENTITY_COLORS } from '../lib/graph-utils'

const TASK_STATUSES = ['todo', 'pending', 'backlog', 'in_progress', 'completed', 'deprecated', 'cancelled'] as const

const STATUS_COLORS: Record<string, string> = {
  todo: '#9ca3af',
  pending: '#ca8a04',
  backlog: '#6b7280',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  deprecated: '#ea580c',
  cancelled: '#dc2626',
}

interface ListViewProps {
  data: GraphData
  onNodeClick: (node: GraphNode) => void
  searchQuery: string
  onTaskStatusChange?: (nodeId: string, status: string) => void
}

type SortMode = 'type' | 'date'
type FilterType = EntityType | 'all'

const TYPE_ORDER: EntityType[] = [
  'note', 'ai_note', 'person', 'project', 'task',
  'mood', 'body', 'food', 'activity', 'resource', 'reflection',
]
const TYPE_LABELS: Record<EntityType, string> = {
  note: 'Notes', ai_note: 'AI Notes', person: 'People', project: 'Projects', task: 'Tasks',
  mood: 'Moods', body: 'Body', food: 'Food', activity: 'Activities',
  resource: 'Resources', reflection: 'Reflections',
}
const TYPE_SHORT: Record<EntityType, string> = {
  note: 'Notes', ai_note: 'AI', person: 'People', project: 'Projects', task: 'Tasks',
  mood: 'Mood', body: 'Body', food: 'Food', activity: 'Activity',
  resource: 'Resources', reflection: 'Reflect',
}

function getNodeDate(node: GraphNode): string {
  const d = node.data as any
  return d.timestamp || d.created_at || ''
}

function getNodeTitle(node: GraphNode): string {
  return node.label
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  return text.slice(0, max).trimEnd() + '...'
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function matchesSearch(node: GraphNode, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  const title = getNodeTitle(node).toLowerCase()
  const desc = (node.description || '').toLowerCase()
  const label = node.label.toLowerCase()
  return title.includes(q) || desc.includes(q) || label.includes(q)
}

function Chip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean
  color?: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
        active
          ? 'text-white border-gray-600 bg-gray-800'
          : 'text-gray-500 border-gray-800 hover:border-gray-700 hover:text-gray-400'
      }`}
      style={active && color ? { borderColor: color + '80', color } : undefined}
    >
      {children}
    </button>
  )
}

function StatusDropdown({ node, onStatusChange }: { node: GraphNode; onStatusChange: (nodeId: string, status: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const status = (node.data as Task).status

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={e => { e.stopPropagation(); setOpen(!open) }}
        className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border transition-colors"
        style={{ color: STATUS_COLORS[status] || '#9ca3af', borderColor: (STATUS_COLORS[status] || '#9ca3af') + '40' }}
      >
        {status.replace('_', ' ')}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[120px]">
          {TASK_STATUSES.map(s => (
            <button
              key={s}
              onClick={e => { e.stopPropagation(); onStatusChange(node.id, s); setOpen(false) }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-gray-800 ${
                s === status ? 'font-medium' : 'opacity-70 hover:opacity-100'
              }`}
              style={{ color: STATUS_COLORS[s] }}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ListView({ data, onNodeClick, searchQuery, onTaskStatusChange }: ListViewProps) {
  const [sortMode, setSortMode] = useState<SortMode>('type')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('all')
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('all')

  // Distinct source_types present in note data
  const availableSourceTypes = useMemo(() => {
    const types = new Set<string>()
    data.nodes.forEach(n => {
      if (n.entityType === 'note') {
        const st = (n.data as Note).source_type
        if (st) types.add(st)
      }
    })
    return Array.from(types).sort()
  }, [data.nodes])

  // Distinct task types
  const availableTaskTypes = useMemo(() => {
    const types = new Set<string>()
    data.nodes.forEach(n => {
      if (n.entityType === 'task') {
        const t = (n.data as Task).type
        if (t) types.add(t)
      }
    })
    return Array.from(types).sort()
  }, [data.nodes])

  // Entity type + source type + task status filter
  const filteredByType = useMemo(() => {
    let nodes = filterType === 'all'
      ? data.nodes
      : data.nodes.filter(n => n.entityType === filterType)

    if (filterType === 'note' && sourceFilter !== 'all') {
      nodes = nodes.filter(n => (n.data as Note).source_type === sourceFilter)
    }
    if (filterType === 'task' && taskStatusFilter !== 'all') {
      nodes = nodes.filter(n => (n.data as Task).status === taskStatusFilter)
    }
    if (filterType === 'task' && taskTypeFilter !== 'all') {
      nodes = nodes.filter(n => (n.data as Task).type === taskTypeFilter)
    }
    return nodes
  }, [data.nodes, filterType, sourceFilter, taskStatusFilter, taskTypeFilter])

  // Search on top of type filter
  const filteredNodes = useMemo(() => {
    if (!searchQuery) return filteredByType
    return filteredByType.filter(n => matchesSearch(n, searchQuery))
  }, [filteredByType, searchQuery])

  const grouped = useMemo(() => {
    const groups: Record<EntityType, GraphNode[]> = {
      note: [], ai_note: [], person: [], project: [], task: [],
      mood: [], body: [], food: [], activity: [], resource: [], reflection: [],
    }
    filteredNodes.forEach(n => groups[n.entityType]?.push(n))
    for (const key of TYPE_ORDER) {
      groups[key].sort((a, b) => new Date(getNodeDate(b)).getTime() - new Date(getNodeDate(a)).getTime())
    }
    return groups
  }, [filteredNodes])

  const sortedByDate = useMemo(() => {
    return [...filteredNodes].sort((a, b) =>
      new Date(getNodeDate(b)).getTime() - new Date(getNodeDate(a)).getTime()
    )
  }, [filteredNodes])

  function handleSetFilterType(type: FilterType) {
    setFilterType(type)
    setSourceFilter('all')
    setTaskStatusFilter('all')
    setTaskTypeFilter('all')
  }

  return (
    <div className="w-full h-full overflow-y-auto bg-black">
      {/* Controls */}
      <div className="sticky top-0 z-10 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        {/* Sort row */}
        <div className="px-4 pt-3 pb-2 flex gap-2 items-center">
          <Chip active={sortMode === 'type'} onClick={() => setSortMode('type')}>By Type</Chip>
          <Chip active={sortMode === 'date'} onClick={() => setSortMode('date')}>By Date</Chip>
          <span className="ml-auto text-xs text-gray-600 self-center">
            {filteredNodes.length}{(searchQuery || filterType !== 'all') ? ` / ${data.nodes.length}` : ''} items
          </span>
        </div>

        {/* Entity type filter row */}
        <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto scrollbar-none">
          <Chip active={filterType === 'all'} onClick={() => handleSetFilterType('all')}>All</Chip>
          {TYPE_ORDER.map(type => (
            <Chip
              key={type}
              active={filterType === type}
              color={ENTITY_COLORS[type]}
              onClick={() => handleSetFilterType(type)}
            >
              {TYPE_SHORT[type]}
            </Chip>
          ))}
        </div>

        {/* Source type sub-filter — only when Notes is selected */}
        {filterType === 'note' && availableSourceTypes.length > 1 && (
          <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto scrollbar-none border-t border-gray-900 pt-2">
            <span className="text-[10px] text-gray-600 self-center mr-1 uppercase tracking-wider">Source</span>
            <Chip active={sourceFilter === 'all'} onClick={() => setSourceFilter('all')}>All</Chip>
            {availableSourceTypes.map(st => (
              <Chip
                key={st}
                active={sourceFilter === st}
                onClick={() => setSourceFilter(st)}
              >
                {st.charAt(0).toUpperCase() + st.slice(1)}
              </Chip>
            ))}
          </div>
        )}

        {/* Task status sub-filter — only when Tasks is selected */}
        {filterType === 'task' && (
          <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto scrollbar-none border-t border-gray-900 pt-2">
            <span className="text-[10px] text-gray-600 self-center mr-1 uppercase tracking-wider">Status</span>
            <Chip active={taskStatusFilter === 'all'} onClick={() => setTaskStatusFilter('all')}>All</Chip>
            {TASK_STATUSES.map(s => (
              <Chip
                key={s}
                active={taskStatusFilter === s}
                color={STATUS_COLORS[s]}
                onClick={() => setTaskStatusFilter(s)}
              >
                {s.replace('_', ' ')}
              </Chip>
            ))}
          </div>
        )}

        {/* Task type sub-filter — only when Tasks is selected and types exist */}
        {filterType === 'task' && availableTaskTypes.length > 0 && (
          <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto scrollbar-none border-t border-gray-900 pt-2">
            <span className="text-[10px] text-gray-600 self-center mr-1 uppercase tracking-wider">Type</span>
            <Chip active={taskTypeFilter === 'all'} onClick={() => setTaskTypeFilter('all')}>All</Chip>
            {availableTaskTypes.map(t => (
              <Chip
                key={t}
                active={taskTypeFilter === t}
                onClick={() => setTaskTypeFilter(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="pb-24">
        {filteredNodes.length === 0 ? (
          <div className="text-center text-gray-600 text-sm py-16">No items match the current filter.</div>
        ) : sortMode === 'type' ? (
          TYPE_ORDER.map(type => {
            const items = grouped[type]
            if (items.length === 0) return null
            return (
              <div key={type}>
                <div
                  className="sticky top-[53px] z-[5] px-4 py-2 text-xs uppercase tracking-wider font-medium bg-black/90 backdrop-blur-sm border-b border-gray-900"
                  style={{ color: ENTITY_COLORS[type] }}
                >
                  {TYPE_LABELS[type]} ({items.length})
                </div>
                {items.map(node => (
                  <NodeRow key={node.id} node={node} onNodeClick={onNodeClick} onTaskStatusChange={onTaskStatusChange} />
                ))}
              </div>
            )
          })
        ) : (
          sortedByDate.map(node => (
            <NodeRow key={node.id} node={node} onNodeClick={onNodeClick} onTaskStatusChange={onTaskStatusChange} />
          ))
        )}
      </div>
    </div>
  )
}

function NodeRow({
  node, onNodeClick, onTaskStatusChange,
}: {
  node: GraphNode
  onNodeClick: (n: GraphNode) => void
  onTaskStatusChange?: (nodeId: string, status: string) => void
}) {
  const color = ENTITY_COLORS[node.entityType]
  const title = getNodeTitle(node)
  const date = formatDate(getNodeDate(node))
  const isTask = node.entityType === 'task'

  const badgeText = node.entityType === 'note'
    ? ((node.data as Note).source_type || 'text')
    : node.entityType.replace('_', ' ')

  return (
    <div className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-900 hover:bg-gray-900/50 transition-colors">
      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <button
        onClick={() => onNodeClick(node)}
        className="flex-1 flex items-center gap-3 text-left min-w-0"
      >
        <span className={`text-sm flex-1 min-w-0 truncate ${
          isTask && (node.data as Task).status === 'completed' ? 'line-through text-gray-600' : 'text-gray-300'
        }`}>
          {truncate(title, 60)}
        </span>
        <span className="text-gray-600 text-[10px] shrink-0">{date}</span>
      </button>
      {isTask && onTaskStatusChange ? (
        <StatusDropdown node={node} onStatusChange={onTaskStatusChange} />
      ) : (
        <span
          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0"
          style={{ color, borderColor: color + '30' }}
        >
          {badgeText}
        </span>
      )}
    </div>
  )
}
